//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { IUrlRouterProvider } from 'angular-ui-router';
import stubTrue from 'lodash/fp/stubTrue';

import { hasPermissionForTask } from '@services/permissions';
import { TWITTER_SCHEDULE_TASK_NAME } from '@twitter/redux/schedule/twitterScheduleModel';

import {
  IPartialState,
  NavGroup,
  StateFactory,
  componentNameToTemplate,
  hasFeature,
  lazyLoadModule,
  lazyLoadSubModule,
} from 'app/initialization/routing/stateHelpers';

// Sets default values for state data
function TwitterStateFactory(state: IPartialState) {
  const accessFn = state.data?.hasAccess || stubTrue;
  const data = {
    title: 'Twitter',
    navGroup: NavGroup.PUBLISHING,
    ...state.data,
    hasAccess: () => hasFeature(FeatureNames.SocialDashboards) && accessFn(),
  };
  return StateFactory({
    ...state,
    data,
  });
}

function lazyLoadTwitterBundle(importFn: () => Promise<unknown>) {
  return lazyLoadSubModule('curalate.twitter', importFn);
}

export function routingRules(urlRouterProvider: IUrlRouterProvider) {
  urlRouterProvider.when('/twitter', '/twitter-v2');
  urlRouterProvider.when('/twitter/schedule', '/twitter-v2/schedule');
  urlRouterProvider.when(
    '/twitter/schedule/timeline',
    '/twitter-v2/schedule/timeline',
  );
  urlRouterProvider.when(
    '/twitter/schedule/post-details?:scheduledPostId&:postTimeMs&:targetCuid',
    '/twitter-v2/schedule/post-details?:scheduledPostId&:postTimeMs&:targetCuid',
  );
  urlRouterProvider.when('/twitter', '/twitter/schedule');
  urlRouterProvider.when('/twitter/schedule', '/twitter/schedule/timeline');
}

export const states = {
  twitter: TwitterStateFactory({
    url: '/twitter',
    template: componentNameToTemplate('twitterApp'),
    resolve: {
      ...lazyLoadModule('curalate.twitter', () => import('./TwitterApp')),
    },
  }),
  'twitter.schedule': TwitterStateFactory({
    url: '/schedule',
    views: {
      'twitter-main': {
        template:
          '<div ui-view="twitter-sub" class="flex-tall cu-h-100"></div>',
      },
    },
  }),
  'twitter.schedule.timeline': TwitterStateFactory({
    url: '/timeline',
    views: {
      'twitter-sub': {
        template: componentNameToTemplate('twitterSchedulePage'),
      },
    },
    resolve: {
      ...lazyLoadTwitterBundle(
        () => import('./components/schedule/TwitterSchedulePage'),
      ),
    },
  }),
  'twitter.schedule.postDetails': TwitterStateFactory({
    url: '/post-details?:scheduledPostId&:postTimeMs&:targetCuid',
    views: {
      'twitter-sub': {
        template: componentNameToTemplate('twitterPostDetailsPage'),
      },
    },
    data: {
      hasAccess: () => hasPermissionForTask(TWITTER_SCHEDULE_TASK_NAME),
    },
    resolve: {
      ...lazyLoadTwitterBundle(
        () => import('./components/post-details/TwitterPostDetailsPage'),
      ),
    },
  }),
};
